import { GenerateABDFATData, useGenerateABDFATs } from "@/api/generate-abd-fat";
import {
  OPMFATData,
  useInReviewValidateFAT,
  useOPMFATGenerate,
  useOPMFATs,
} from "@/api/opm-fat";
import { OPMFDTData } from "@/api/opm-fdt";
import {
  OPMFATValidationSchema,
  useOPMFATValidate,
  useRFS,
  useRFSStatusUpdate,
} from "@/api/rfs";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2Icon,
  CheckSquare2,
  LucideUpload,
  XCircleIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { ROLE, STATUS } from "@/utils/constant";
import { swalErrorDefault, swalLoading, swalSuccess } from "@/utils/lib/swal";
import axios from "axios";
import { FormContainer } from "@/components/form/form-container";
import { ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { FieldFile } from "@/components/form/field-file";
import { DefaultButton } from "@/components/ui/button";
import { useState } from "react";
import { MediaData } from "@/api/media";
import { useAllowEditOuputFat } from "@/api/master-param";
import { ImageWithPreview } from "./ImageWithPreview";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useImageCompressor } from "@/utils/hook/useImageCompressor";
import { useValidation } from "./contexts/validationContext";
import { createRFSStatusUpdater } from "./utils/rfsStatusUpdater";
import {
  FAT_ERROR_FIELD_TO_STATUS,
  overrideFatInvalid,
  overrideFatValid,
  statusFieldsFromErrors,
} from "./utils/dataMappers";
import { OverallStatus } from "./providers/ValidationProvider";

interface FATAccordionItemsProps {
  abdFats?: GenerateABDFATData[];
  opmFdts?: OPMFDTData[];
  opmFats?: OPMFATData[];
  id: string;
  setKey: React.Dispatch<React.SetStateAction<number>>;
}
export function FATAccordionItems({
  abdFats,
  opmFdts,
  opmFats,
  id,
  setKey,
}: FATAccordionItemsProps) {
  const { data: allowEditOutputFats } = useAllowEditOuputFat();
  const isEditFat = allowEditOutputFats?.content[0].active;
  if (!abdFats || !opmFdts || !opmFats) {
    return null;
  }
  return (
    <>
      {opmFdts?.[0] &&
        abdFats?.map((abdFat) => {
          const opmFat = opmFats.find(
            (opmFat) => opmFat.fatId === abdFat.fatId,
          );
          return (
            <AccordionItem value={abdFat.id} key={abdFat.id}>
              <AccordionTrigger>
                <span className="flex gap-2 dark:text-neutral-300">
                  FAT ID: {abdFat.fatId}
                  {opmFats?.find(
                    (opmFat) =>
                      opmFat.fatId === abdFat.fatId && opmFat.valid === true,
                  ) && <CheckSquare2 color="#5dee7a" />}
                </span>
              </AccordionTrigger>
              <AccordionContent className="flex gap-x-2">
                <FATForm
                  opmFat={opmFat}
                  opmFats={opmFats}
                  rfsId={id}
                  fatId={abdFat.fatId}
                  opmFdtId={opmFdts?.[0].id}
                  isEditFat={isEditFat}
                  setKey={setKey}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
    </>
  );
}

function FATForm({
  opmFat,
  opmFats,
  rfsId,
  fatId,
  opmFdtId,
  isEditFat,
  setKey,
}: {
  opmFat?: OPMFATData;
  opmFats?: OPMFATData[];
  rfsId: string;
  fatId: string;
  opmFdtId: string;
  isEditFat?: boolean;
  setKey: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [poleFatMedia, setPoleFatMedia] = useState<MediaData | undefined>(
    opmFat?.poleFatMedia,
  );
  const [photoFatMedia, setPhotoFatMedia] = useState<MediaData | undefined>(
    opmFat?.imageFatMedia,
  );
  const [outputFatMedia, setOutputFatMedia] = useState<MediaData | undefined>(
    opmFat?.outputFatMedia,
  );
  const [splitterFatMedia, setSplitterFatMedia] = useState<
    MediaData | undefined
  >(opmFat?.splitterFatMedia);

  // OCR Result
  const [ocrOutputValue, setOcrOutputValue] = useState(
    opmFat?.outputFatText ?? "",
  );
  const [ocrFatId, setOcrFatId] = useState(opmFat?.fatIdText ?? "");

  const form = useForm({
    mode: "onChange",
  });

  const queryClient = useQueryClient();
  const { isFetching: isOpmFatsLoading } = useOPMFATs({ opmFdtRfsID: rfsId });
  const { isFetching: isAbdFatsLoading } = useGenerateABDFATs({ rfsId });

  const currentPoleFat = form.watch("tiang_fat");
  const currentPhotoFat = form.watch("foto_fat");
  const currentOutputFat = form.watch("output_fat");
  const currentSplitterFat = form.watch("splitter_fat");
  const poleFatPath = currentPoleFat?.stream || opmFat?.poleFatMedia?.stream;
  const photoFatPath = currentPhotoFat?.stream || opmFat?.imageFatMedia?.stream;
  const outputFatPath =
    currentOutputFat?.stream || opmFat?.outputFatMedia?.stream;
  const splitterFatPath =
    currentSplitterFat?.stream || opmFat?.splitterFatMedia?.stream;
  const hasAllImages =
    poleFatPath && photoFatPath && outputFatPath && splitterFatPath;

  const { mutateAsync: compress } = useImageCompressor();

  const { data: rfs } = useRFS({ id: rfsId });
  const { mutate: generate, isPending: isGenerating } = useOPMFATGenerate();
  const { mutate: submit, isPending: isSubmitting } = useOPMFATValidate();
  const { mutate: update, isPending: isUpdating } = useInReviewValidateFAT();
  const { mutate: updateStatus } = useRFSStatusUpdate();

  const {
    requiredList,
    unrequiredList,
    overallStatus: currentOverallStatus,
    getStatusForSubmit,
  } = useValidation();
  const { roles } = useCurrentUser();
  const canReview = hasCommonElement(roles, [
    ROLE.ADMINISTRATOR,
    ROLE.REVISE_DOCUMENT_OFFICER,
    ROLE.VENDOR,
    ROLE.VALIDATOR_DOCUMENT_OFFICER,
  ]);
  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);

  const isReview = rfs?.statusRfsSubmission === STATUS.RFS.REVISION_BY_LN;
  const isReadonly =
    rfs?.statusRfsSubmission === STATUS.RFS.GENERATING_HPDB || isViewOnly;
  const isOcrNewInput =
    ocrOutputValue !== (opmFat?.outputFatText ?? "") ||
    ocrFatId !== (opmFat?.fatIdText ?? "");

  const isDisabledGenerate =
    !hasAllImages ||
    isReview ||
    !canReview ||
    isOpmFatsLoading ||
    isAbdFatsLoading ||
    isReadonly;
  const isDisabledSubmit =
    !hasAllImages ||
    !ocrOutputValue ||
    !ocrFatId ||
    isGenerating ||
    (isReview && !isOcrNewInput) || // disable submit button ketika user dalam proses review dan tidak melakukan perubahan pada OCR output text atau fat id
    !canReview ||
    isOpmFatsLoading ||
    isAbdFatsLoading ||
    isReadonly;

  const acceptedExtensions = ".jpg, .jpeg";

  function handleStatus(nextStatus: OverallStatus) {
    const statusUpdater = createRFSStatusUpdater({
      rfsId,
      overallStatus: nextStatus,
      rfsData: rfs,
      updateStatusMutation: updateStatus,
      prefix: "opm",
      options: {
        onError: () => {
          swalErrorDefault.fire({
            title: "Status Update Failed",
            text: "You failed to update the RFS status. Please try again.",
          });
        },
      },
    });
    statusUpdater();
  }

  function getDisableSubmit() {
    const isThereOCRFat = [...requiredList, ...unrequiredList].some(
      (item) => item.code === "A11",
    );
    return isThereOCRFat ? isDisabledSubmit : isDisabledGenerate;
  }

  function handleFileDragOver(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
  }

  function handleFileDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (!files.length) {
      return;
    }
    if (files.length > 1) {
      throw new Error("Only one file is allowed");
    }

    const changeEvent = new Event("change", { bubbles: true });
    const input = e.currentTarget.querySelector("input");
    if (!input) {
      return;
    }
    input.files = files;
    input.dispatchEvent(changeEvent);

    e.dataTransfer.clearData();
  }

  function handleGenerate() {
    swalLoading.fire();
    generate(
      {
        imageFatMedia: { path: photoFatMedia?.path },
        outputFatMedia: { path: outputFatMedia?.path },
        fatId: fatId,
      },
      {
        onSuccess: (result) => {
          setOcrFatId(result.fatIdText ?? "");
          setOcrOutputValue(result.outputFatText ?? "");
          swalLoading.close();
        },
        onError: () => {
          setOcrFatId("");
          setOcrOutputValue("");
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleSubmit() {
    if (
      !ocrOutputValue ||
      !poleFatMedia ||
      !photoFatMedia ||
      !outputFatMedia ||
      !splitterFatMedia ||
      !ocrFatId
    ) {
      console.error("Missing required data: ", {
        ocrOutputValue,
        poleFatMedia,
        photoFatMedia,
        outputFatMedia,
        splitterFatMedia,
        ocrFatId,
      });
      return;
    }
    const submitAction = isReview ? update : submit;

    swalLoading.fire();
    const data: OPMFATValidationSchema = {
      opmFat: {
        fatId: fatId,
        fatIdText: ocrFatId,
        outputFatText: ocrOutputValue,
        opmFdt: {
          id: opmFdtId,
        },
        poleFatMedia: {
          id: poleFatMedia.id,
          path: poleFatMedia.path,
          originalFileName: poleFatMedia.originalFileName,
        },
        imageFatMedia: {
          id: photoFatMedia.id,
          path: photoFatMedia.path,
          originalFileName: photoFatMedia.originalFileName,
        },
        outputFatMedia: {
          id: outputFatMedia.id,
          path: outputFatMedia.path,
          originalFileName: outputFatMedia.originalFileName,
        },
        splitterFatMedia: {
          id: splitterFatMedia.id,
          path: splitterFatMedia.path,
          originalFileName: splitterFatMedia.originalFileName,
        },
      },
    };
    if (opmFat?.id) {
      data.opmFat.id = opmFat.id;
    }
    submitAction(
      {
        id: rfsId,
        data: data,
      },
      {
        onSuccess: () => {
          form.clearErrors();
          swalSuccess.fire({
            title: "Validation Successful",
            text: "You have successfully submitted an FAT.",
          });

          // Cache opmFats belum berisi FAT ini saat onSuccess, sedangkan A05-A07
          // membandingkan jumlahnya dengan abdFatCount. Hitung dari data override.
          const nextStatus =
            getStatusForSubmit?.({
              opmFats: overrideFatValid(opmFats, fatId),
            })?.overallStatus ?? currentOverallStatus;
          handleStatus(nextStatus);
        },
        onError: (error) => {
          form.clearErrors();
          if (axios.isAxiosError(error)) {
            const messages: { [key: string]: string }[] =
              error.response?.data?.data ?? [];

            let poleFatMessage = "";
            let imageFatMessage = "";
            let outputFatMessage = "";
            let splitterFatMessage = "";
            let outputFatTextMessage = "";
            let fatIdTextMessage = "";

            for (const message of messages) {
              if (message.poleFatMedia) {
                poleFatMessage += message.poleFatMedia;
              }
              if (message.imageFatMedia) {
                imageFatMessage += message.imageFatMedia + ". ";
              }
              if (message.fatIdText) {
                fatIdTextMessage += message.fatIdText + ". ";
              }
              if (message.outputFatMedia) {
                outputFatMessage += message.outputFatMedia + ". ";
              }
              if (message.outputFatText) {
                outputFatTextMessage += message.outputFatText + ". ";
              }
              if (message.splitterFatMedia) {
                splitterFatMessage += message.splitterFatMedia;
              }
            }

            form.setError("tiang_fat", {
              message: poleFatMessage,
            });

            form.setError("foto_fat", {
              message: `${imageFatMessage}${fatIdTextMessage}`,
            });
            form.setError("output_fat", {
              message: `${outputFatMessage}${outputFatTextMessage}`,
            });
            form.setError("splitter_fat", {
              message: splitterFatMessage,
            });

            // Submit ditolak: cermin dari onSuccess — tambal data secara lokal lalu
            // hitung ulang, tanpa menunggu refetch. onSettled yang menyegarkan UI.
            if (error.response?.status === 400) {
              const failedFields = statusFieldsFromErrors<OPMFATData>(
                error.response?.data?.data,
                FAT_ERROR_FIELD_TO_STATUS,
              );
              const nextStatus =
                getStatusForSubmit?.({
                  opmFats: overrideFatInvalid(opmFats, fatId, failedFields),
                })?.overallStatus ?? currentOverallStatus;
              handleStatus(nextStatus);
            }
          }

          swalErrorDefault.fire({
            title: "Validation Failed",
            text: "You failed to perform data validation. Please check the data then try again.",
          });
        },
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: ["generateABDFATsCount", { rfsId }],
          });

          queryClient.invalidateQueries({
            queryKey: ["opmFats-infinite", { opmFdtRfsID: rfsId }],
          });

          queryClient.invalidateQueries({
            queryKey: ["generateABDFATs-infinite"],
          });

          setKey((prev) => prev + 1);
        },
      },
    );
  }

  return (
    <>
      <FormContainer form={form} className="w-full">
        <ImageList cols={5} rowHeight={240} className="flex-grow">
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FAT Pole{" "}
                  <ImageStatusIcon valid={opmFat?.poleFatStatus ?? null} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentPoleFat?.path || opmFat?.poleFat) && (
              <ImageWithPreview src={poleFatPath} alt="Tiang FAT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.tiang_fat && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.tiang_fat?.message?.toString()}
                </span>
              )}
              {!isGenerating && (
                <label
                  className={`mt-auto flex cursor-pointer flex-col border-2 border-dashed py-2 text-center dark:text-neutral-300 ${
                    isReadonly && "sr-only"
                  }`}
                  onDragOver={handleFileDragOver}
                  onDrop={handleFileDrop}
                >
                  <LucideUpload className="mx-auto" />
                  click or drag here
                  <FieldFile
                    isVisuallyHidden
                    name="tiang_fat"
                    label="Tiang FAT"
                    media={{
                      target: "/opm/pole",
                      after: (result) => setPoleFatMedia(result),
                      before: (files) => {
                        const compressedFile = compress(files[0]);
                        return compressedFile;
                      },
                    }}
                    accept={acceptedExtensions}
                  />
                </label>
              )}
            </div>
          </ImageListItem>
          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FAT Photo{" "}
                  <ImageStatusIcon valid={opmFat?.imageFatStatus ?? null} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentPhotoFat?.path || opmFat?.imageFat) && (
              <ImageWithPreview src={photoFatPath} alt="Foto FAT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.foto_fat && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.foto_fat?.message?.toString()}
                </span>
              )}
              <input
                className="w-full border-2 text-neutral-950"
                value={ocrFatId}
                onChange={(e) => {
                  setOcrFatId(e.target.value);
                }}
                disabled={!isEditFat}
              />
              {!isGenerating && (
                <label
                  className={`mt-auto flex cursor-pointer flex-col border-2 border-dashed py-2 text-center dark:text-neutral-300 ${
                    isReadonly && "sr-only"
                  }`}
                  onDragOver={handleFileDragOver}
                  onDrop={handleFileDrop}
                >
                  <LucideUpload className="mx-auto" />
                  click or drag here
                  <FieldFile
                    isVisuallyHidden
                    name="foto_fat"
                    label="Foto FAT"
                    media={{
                      target: "/opm/photo",
                      after: (result) => {
                        setPhotoFatMedia(result);
                        setOcrFatId("");
                      },
                      before: (files) => {
                        const compressedFile = compress(files[0]);
                        return compressedFile;
                      },
                    }}
                    accept={acceptedExtensions}
                  />
                </label>
              )}
            </div>
          </ImageListItem>

          <ImageListItem>
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  Splitter FAT Photo{" "}
                  <ImageStatusIcon valid={opmFat?.splitterFatStatus ?? null} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />
            {(currentSplitterFat?.path || opmFat?.splitterFat) && (
              <ImageWithPreview src={splitterFatPath} alt="Splitter FAT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.splitter_fat && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.splitter_fat?.message?.toString()}
                </span>
              )}
              {!isGenerating && (
                <label
                  className={`mt-auto flex cursor-pointer flex-col border-2 border-dashed py-2 text-center dark:text-neutral-300 ${
                    isReadonly && "sr-only"
                  }`}
                  onDragOver={handleFileDragOver}
                  onDrop={handleFileDrop}
                >
                  <LucideUpload className="mx-auto" />
                  click or drag here
                  <FieldFile
                    isVisuallyHidden
                    name="splitter_fat"
                    label="Splitter FAT"
                    media={{
                      target: "/opm/splitter",
                      after: (result) => setSplitterFatMedia(result),
                      before: (files) => {
                        const compressedFile = compress(files[0]);
                        return compressedFile;
                      },
                    }}
                    accept={acceptedExtensions}
                  />
                </label>
              )}
            </div>
          </ImageListItem>

          <ImageListItem className="flex flex-col gap-y-2">
            <ImageListItemBar
              title={
                <span className="flex items-center gap-1">
                  FAT Output{" "}
                  <ImageStatusIcon valid={opmFat?.outputFatStatus ?? null} />
                </span>
              }
              className="!bg-slate-900/30"
              position="top"
            />

            {(currentOutputFat?.path || opmFat?.outputFat) && (
              <ImageWithPreview src={outputFatPath} alt="Output FAT" />
            )}
            <div className="mt-auto">
              {form.formState.errors.output_fat && (
                <span className="text-sm font-medium text-red-500 dark:text-red-900">
                  {form.formState.errors.output_fat?.message?.toString()}
                </span>
              )}
              <input
                className="w-full border-2 text-neutral-950"
                value={ocrOutputValue}
                onChange={(e) => {
                  const lastData = (
                    e.nativeEvent as unknown as { data: string | null }
                  ).data;
                  if (lastData && !/[\d.-]/.test(lastData)) {
                    return;
                  }
                  setOcrOutputValue(e.target.value);
                }}
                disabled={!isEditFat}
              />
              {!isGenerating && (
                <label
                  className={`mt-auto flex cursor-pointer flex-col border-2 border-dashed py-2 text-center dark:text-neutral-300 ${
                    isReadonly && "sr-only"
                  }`}
                  onDragOver={handleFileDragOver}
                  onDrop={handleFileDrop}
                >
                  <LucideUpload className="mx-auto" />
                  click or drag here
                  <FieldFile
                    isVisuallyHidden
                    name="output_fat"
                    label="Output FAT"
                    media={{
                      target: "/opm/output",
                      after: (result) => {
                        setOutputFatMedia(result);
                        setOcrOutputValue("");
                      },
                      before: (files) => {
                        const compressedFile = compress(files[0]);
                        return compressedFile;
                      },
                    }}
                    accept={acceptedExtensions}
                  />
                </label>
              )}
            </div>
          </ImageListItem>
        </ImageList>
      </FormContainer>
      <div className="flex w-24 flex-col justify-center gap-y-2">
        <DefaultButton
          type="button"
          mood="axiata-blue-outline"
          className="w-full"
          disabled={isDisabledGenerate}
          loading={isGenerating || isOpmFatsLoading || isAbdFatsLoading}
          onClick={handleGenerate}
          title={isReview ? "In the review process" : undefined}
        >
          Generate
        </DefaultButton>
        <DefaultButton
          type="button"
          mood="axiata-blue"
          className="w-full"
          disabled={getDisableSubmit()}
          loading={
            isUpdating || isSubmitting || isOpmFatsLoading || isAbdFatsLoading
          }
          onClick={handleSubmit}
          title={isReview ? "In the review process" : undefined}
        >
          Submit
        </DefaultButton>
      </div>
    </>
  );
}

function ImageStatusIcon({ valid }: { valid: boolean | null }) {
  if (valid === null || valid === undefined) {
    return null;
  }
  if (valid === true) {
    return <CheckCircle2Icon size={16} className="text-green-500" />;
  }
  return <XCircleIcon size={16} className="text-red-500" />;
}
